/**
 * ตัวช่วยแบบ pure ของการ sync สต๊อกด้วย checkpoint (ไม่ import อะไรเลย เทสต์ได้ตรง ๆ)
 *
 * ภาพรวม (ฝั่ง server ดู microservices/inventory/v2/*)
 *   - ทุกครั้งที่ ledger ถูกแก้ server ต่อท้าย log ที่มีเลข seq เรียงต่อกันไม่มีรู
 *   - เครื่องจำ checkpoint (เลข seq ล่าสุดที่ได้) ตัวเดียว
 *   - POST /inventories-v2/sync { checkpoint } -> ได้แถวปัจจุบันของ key ที่เปลี่ยน (rows)
 *     กับ key ที่ถูกลบ (deleted) ใน response เดียว หรือ status 'reload' = ต้องโหลดใหม่หมด
 */

export interface StockRow {
  key2?: string;
  businessDate?: string;
  createdAt?: string;
  [field: string]: any;
}

export interface StockCacheMeta {
  storeId: string;
  /** commit แถวทั้งร้านครบแล้ว ห้าม incremental ถ้ายังไม่ complete */
  complete: boolean;
  /** เลข seq ล่าสุดของ change log ที่แถวในเครื่องครอบคลุมแล้ว */
  checkpoint: number;
  /** นาฬิกาเครื่อง ใช้นับอายุแคชอย่างเดียว */
  fullLoadCompletedAt: number;
  schemaVersion: number;
  rowCount: number;
}

export type SyncResponse =
  | { status: "ok"; checkpoint: number; rows?: StockRow[]; deleted?: string[] }
  | { status: "reload"; reason?: string };

/** แคชอยู่ได้นานสุด (ตรงกับที่เจ้าของระบบกำหนด ~7 วัน) */
export const MAX_CACHE_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * key ของแถว = sort key ของ DynamoDB (`{businessDate}:{createdAt}`)
 * ประกอบแบบเดียวกับ inventoriesTransactionDataService ฝั่ง server
 */
export const ledgerKey2 = (row: StockRow): string =>
  row.key2 || `${row.businessDate}:${row.createdAt}`;

/** ใส่แถวที่เปลี่ยนทับตาม key2 แล้วลบ key ที่ถูกลบ  คืน Map ใหม่ ไม่แก้ของเดิม */
export const applyChanges = (
  current: Map<string, StockRow>,
  rows: StockRow[] = [],
  deleted: string[] = []
): Map<string, StockRow> => {
  const next = new Map(current);
  for (const key of deleted) next.delete(key);
  for (const row of rows) {
    const key = ledgerKey2(row);
    next.set(key, { ...row, key2: key });
  }
  return next;
};

/**
 * ต้องโหลดใหม่ทั้งหมดไหม (ก่อนจะลอง incremental)
 * คืนเหตุผลเป็น string หรือ null = ใช้แคชต่อได้
 */
export const fullLoadReason = (
  meta: StockCacheMeta | null | undefined,
  nowMs: number,
  schemaVersion: number,
  maxAgeMs: number = MAX_CACHE_AGE_MS
): string | null => {
  if (!meta) return "no-cache";
  if (!meta.complete) return "incomplete";
  if (meta.schemaVersion !== schemaVersion) return "schema-changed";
  if (!Number.isInteger(meta.checkpoint) || meta.checkpoint < 0) return "bad-checkpoint";
  const age = nowMs - Number(meta.fullLoadCompletedAt || 0);
  // นาฬิกาเครื่องถอยหลัง (age ติดลบ) ก็ถือว่าเชื่อไม่ได้
  if (!(age >= 0 && age <= maxAgeMs)) return "expired";
  return null;
};
